/**
 * Copy, thresholds and test ids for the "Reference Documents" row (shared by Audited and
 * Provisional Financials). The row scaffold (label cell + one `<td>` per value header) is
 * rendered by AuditedStatementTable from `headers[].sourceFiles` + `tableData.sourceFilesStatus`;
 * this component only renders ONE cell's worth of content.
 */

/** Row label for the first (Classification-aligned) column. Reused verbatim as the bold title
 *  in the "+ N more" popover's card header — see SourceFileTag.tsx. */
export const REFERENCE_DOCUMENTS_ROW_LABEL = 'Reference Documents';

/** How many source files show inline before the rest collapse behind "+ N more". Always 1 today
 *  — the first file is shown inline (truncated, itself focusable) and everything past it
 *  collapses. */
export const INLINE_NAME_MAX_FILES = 1;

/** A resolved period with zero source files — same em dash every blank cell in the statement
 *  table uses. Deliberately NOT the same as SOURCE_UNAVAILABLE_LABEL: this means the period
 *  resolved with genuinely no file; that one means the lookup FAILED (a babler outage). */
export const NOT_AVAILABLE_LABEL = '—';

export const SOURCE_UNAVAILABLE_LABEL = 'Source unavailable';
export const SOURCE_UNAVAILABLE_TOOLTIP = 'Could not load source files for this period.';

/**
 * Visible characters of the inline file name before it is cut (passed as `limit` to
 * `truncateByCharacters`). The cell was sizing the whole financial-year column to the file name;
 * a hard character cap pins it instead of letting CSS ellipsis grow to its max-width. The full
 * name stays in the hover tooltip and in the "+ N more" popover, which is where it is meant to be
 * read.
 */
export const CELL_NAME_MAX_CHARS = 12;

/** Visible text of the "+ N more" disclosure trigger — N is always the REMAINING file count (the
 *  first file is already shown inline, so this never double-counts it). */
export const getMoreFilesLabel = (count: number): string => `+ ${count} more`;

/**
 * The "+ N more" trigger's only visible text is that count, so its accessible name needs to spell
 * out what activating it does. `getMoreFilesLabel` is a literal PREFIX of the result (WCAG 2.5.3
 * Label in Name — a voice-control user saying "click plus 2 more" must still match).
 */
export const getMoreFilesAccessibleLabel = (count: number): string => `${getMoreFilesLabel(count)} file${count === 1 ? '' : 's'}`;

/**
 * Grace window before the "+ N more" popover closes on mouseleave/blur, so the pointer/focus can
 * move from the trigger into the popover without it disappearing mid-move. Mirrors TooltipV2's
 * 100ms debounce, but held per-component-instance (a local ref) rather than a shared timer — see
 * useSourceFileTagLogic's docblock.
 */
export const MULTI_FILE_LIST_CLOSE_DELAY_MS = 100;

/** Vertical gap (px) between the trigger and the popover list; mirrors TooltipV2's own 10px
 *  offset for its 'bottom' placement. */
export const MULTI_FILE_LIST_OFFSET_PX = 10;

/**
 * Viewport clamp for the popover, computed at open time from the trigger's rect (nothing is
 * rendered yet to measure). Width/height mirror this component's own `.Popover` CSS (`max-width:
 * 25rem`, `max-height: 18.75rem`) as a worst-case bound, not the popover's actual (often smaller)
 * size — keep in sync with that CSS. `EDGE_PADDING_PX` mirrors TooltipV2's own `EDGE_PADDING`.
 */
export const MULTI_FILE_LIST_MAX_WIDTH_PX = 400;
export const MULTI_FILE_LIST_MAX_HEIGHT_PX = 300;
export const MULTI_FILE_LIST_EDGE_PADDING_PX = 12;

export const REFERENCE_DOC_TEST_IDS = {
    PRIMARY_FILE: 'reference-doc-primary-file',
    MORE_FILES_TRIGGER: 'reference-doc-more-files-trigger',
    MORE_FILES_LIST: 'reference-doc-more-files-list',
    MORE_FILES_HEADER: 'reference-doc-more-files-header',
    MORE_FILES_ROW: 'reference-doc-more-files-row',
    NOT_AVAILABLE: 'reference-doc-not-available',
    FAILURE: 'reference-doc-failure',
} as const;
